/**
 * "Your requests" (docs/ux/project-tab-spec.md §1.3): every barked item is a
 * live object. The aggregate line is joined on this device from the item's targets, tasks and
 * run state; rows that need you or got blocked bubble back to the top of Active.
 */
import type { BarkRecord } from '@viko/protocol'
import { stepLabel } from '@viko/transcript-ui'
import { CornerUpRight, MoreHorizontal, Undo2 } from 'lucide-react'
import { type KeyboardEvent, useMemo, useRef, useState } from 'react'
import {
  type AggContext,
  BARK_GLYPH_LABEL,
  type BarkAggregate,
  type BarkFilter,
  barkAggregate,
  canUndo,
  matchesFilter,
  sortBarks,
} from '../../lib/bark-aggregate.js'
import { searchBarks } from '../../lib/bark-outcomes.js'
import { useRunMaterial } from '../../lib/session.js'
import { Keys } from '../Keys.js'
import { useIsSelected } from '../lanes/selection.js'
import { Ago, errorText, useToast } from '../ui.js'
import { BarkGlyph } from './BarkGlyph.js'
import { useAggContext, useBarks } from './useBarks.js'

export interface OutcomeFeedProps {
  projectId: string
  projectName: string
  /** Rows to show (the rail shows the latest few; the full feed passes a large limit). */
  limit: number
  /** Opens an inspector in this tab: `bark:<id>`, `task:<id>`, `run:<id>`, or `barks` (full feed). */
  onInspect: (key: string) => void
  /** Rail = compact by default; the full feed defaults to expanded. */
  defaultDensity?: 'compact' | 'expanded'
}

const FILTERS: { id: BarkFilter; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'needs', label: 'Needs you' },
  { id: 'done', label: 'Done' },
  { id: 'all', label: 'All' },
]

/** Outline the lane cards a bark produced (`data-bark-target` on the Lanes cards). */
export function highlightTargets(ids: string[]) {
  for (const el of document.querySelectorAll('[data-bark-target].vk-bark-hl'))
    el.classList.remove('vk-bark-hl')
  for (const id of ids)
    for (const el of document.querySelectorAll(`[data-bark-target="${CSS.escape(id)}"]`))
      el.classList.add('vk-bark-hl')
}

/** The triage agent's live steps while it works out where a bark goes. */
function TriageSteps({ runId }: { runId: string }) {
  // materialized-state: an outcome feed reads the triage run's snapshot, never its events.
  const view = useRunMaterial(runId)
  const steps = view.activity.steps.slice(-3)
  if (!steps.length) return <span>Reading project context…</span>
  return (
    <span>
      {steps.map((s, i) => (
        <span key={s.id}>
          {i ? ' · ' : ''}
          {stepLabel(s, view.session.cwd)} {s.running ? '…' : '✓'}
        </span>
      ))}
    </span>
  )
}

export function BarkRow({
  bark,
  agg,
  expanded,
  queuedOffline,
  onOpen,
  onUndo,
  onRedirect,
}: {
  bark: BarkRecord
  agg: BarkAggregate
  expanded: boolean
  queuedOffline: boolean
  onOpen: () => void
  onUndo?: (() => void) | undefined
  onRedirect: () => void
}) {
  const targets = bark.actions.flatMap((a) => a.targets.map((t) => t.id))
  const needs = agg.needsYou > 0
  const selected = useIsSelected('bark', bark.id) // now-inbox: the inspected item
  return (
    <li
      aria-current={selected ? 'true' : undefined}
      className={`group grid gap-1 rounded-lg px-2 py-2 hover:bg-surface-2 focus-within:bg-surface-2${selected ? ' is-selected bg-accent-soft shadow-[inset_3px_0_0_var(--vk-accent)]' : ''}`}
      onMouseEnter={() => highlightTargets(targets)}
      onMouseLeave={() => highlightTargets([])}
      data-bark-row={bark.id}
    >
      <div className="flex items-baseline gap-2">
        <BarkGlyph glyph={agg.glyph} />
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left text-[15px] text-text-1"
          data-bark-open
          onFocus={() => highlightTargets(targets)}
          onBlur={() => highlightTargets([])}
          onClick={onOpen}
          aria-label={`“${bark.title ?? bark.text}”, ${BARK_GLYPH_LABEL[agg.glyph]}: ${agg.line}`}
        >
          “{bark.title ?? (bark.text || 'Attachment')}”
        </button>
        <span className="flex-none text-[14px] text-text-3">
          <Ago ts={bark.createdAt} />
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-[22px] text-[14px] text-text-2">
        <span className={`min-w-0 flex-1 ${needs ? 'text-status-wait' : ''}`}>
          {queuedOffline ? (
            'Queued offline — sends when you’re back'
          ) : bark.status === 'triaging' && bark.triage.routerRunId ? (
            <TriageSteps runId={bark.triage.routerRunId} />
          ) : (
            <>▸ {agg.line}</>
          )}
        </span>
        {onUndo ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[14px] text-text-2"
            onClick={onUndo}
          >
            <Undo2 size={13} aria-hidden="true" /> Undo
          </button>
        ) : null}
        {bark.status !== 'queued' && bark.status !== 'triaging' ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[14px] text-text-2"
            onClick={onRedirect}
          >
            <CornerUpRight size={13} aria-hidden="true" /> Redirect
          </button>
        ) : null}
        <button type="button" aria-label="Details" className="text-text-3" onClick={onOpen}>
          <MoreHorizontal size={14} aria-hidden="true" />
        </button>
      </div>
      {agg.reason && needs ? (
        <p className="m-0 pl-[22px] text-[14px] text-status-wait">{agg.reason}</p>
      ) : null}
      {expanded && bark.interpretation ? (
        <p className="m-0 pl-[22px] text-[14px] text-text-2">{bark.interpretation}</p>
      ) : null}
    </li>
  )
}

export function OutcomeFeed({
  projectId,
  limit,
  onInspect,
  defaultDensity = 'compact',
}: OutcomeFeedProps) {
  const { data, store } = useBarks(projectId)
  const ctx: AggContext = useAggContext(projectId)
  const toast = useToast()
  const [filter, setFilter] = useState<BarkFilter>('active')
  const [density, setDensity] = useState(defaultDensity)
  // outcome-polish: the full feed (expanded by default) is searchable.
  const [query, setQuery] = useState('')
  const searchable = defaultDensity === 'expanded'
  const list = useRef<HTMLUListElement>(null)
  const now = Date.now()
  const rows = useMemo(
    () => sortBarks((data ?? []).map((bark) => ({ bark, agg: barkAggregate(bark, ctx) }))),
    [data, ctx],
  )
  const counts = Object.fromEntries(
    FILTERS.map((f) => [f.id, rows.filter((r) => matchesFilter(r.agg, f.id, now)).length]),
  ) as Record<BarkFilter, number>
  const shown = searchBarks(
    rows.filter((r) => matchesFilter(r.agg, filter, now)),
    searchable ? query : '',
  )
  const visible = shown.slice(0, limit)

  const undo = async (bark: BarkRecord) => {
    try {
      await store?.control(bark, 'undo')
      toast(
        bark.actions.some((a) => a.ops.some((o) => o.op === 'run.steered'))
          ? 'Undone. The run got a note to disregard your message.'
          : 'Undone',
      )
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  const onKey = (e: KeyboardEvent<HTMLUListElement>) => {
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>('[data-bark-open]') ?? [])]
    const i = items.indexOf(document.activeElement as HTMLButtonElement)
    const row = i >= 0 ? visible[i] : undefined
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next =
        items[Math.max(0, Math.min(items.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))]
      next?.focus()
    } else if (row && e.key === 'u' && canUndo(row.bark, now)) void undo(row.bark)
    else if (row && e.key === 'r') onInspect(`bark:${row.bark.id}:redirect`)
  }

  // The tab's rail is hidden until something was said (the composer placeholder explains it);
  // the full feed (inspector) keeps its empty line.
  if (!searchable && !rows.length) return null
  // Filters take 1–4 while focus is in the feed.
  const onFeedKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return
    if ((e.target as HTMLElement).closest('input, textarea, [contenteditable="true"]')) return
    const f = FILTERS[Number(e.key) - 1]
    if (!f || e.key.length !== 1) return
    e.preventDefault()
    setFilter(f.id)
  }

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: 1–4 pick a filter while focus is inside
    <section
      aria-labelledby={`bark-feed-${projectId}`}
      className="lk-feedsec grid gap-2"
      onKeyDown={onFeedKey}
    >
      <div className="lk-section-h">
        <h2 id={`bark-feed-${projectId}`}>Your requests</h2>
        <button
          type="button"
          className="lk-count"
          aria-label={`All ${rows.length}: open the full feed`}
          onClick={() => onInspect('barks')}
        >
          {rows.length}
        </button>
        <button
          type="button"
          className="lk-head-link"
          aria-pressed={density === 'expanded'}
          onClick={() => setDensity(density === 'compact' ? 'expanded' : 'compact')}
        >
          {density === 'compact' ? 'Expand' : 'Compact'}
        </button>
      </div>
      {searchable ? (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search your requests"
          placeholder="Search your requests"
          className="rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-[15px] text-text-1"
          data-bark-search
        />
      ) : null}
      {rows.length ? (
        <div role="tablist" aria-label="Filter" className="lk-filters flex flex-wrap gap-1">
          {FILTERS.map((f, i) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              aria-keyshortcuts={String(i + 1)}
              aria-label={counts[f.id] ? `${f.label} ${counts[f.id]}` : f.label}
              className={`rounded-md px-2 py-0.5 text-[14px] ${
                filter === f.id ? 'bg-surface-2 text-text-1' : 'text-text-2'
              }`}
              onClick={() => setFilter(f.id)}
            >
              <Keys k={String(i + 1)} className="lk-fkey" />
              {f.label}
              {counts[f.id] ? (
                <span className="tabular-nums text-text-3"> {counts[f.id]}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
      {visible.length ? (
        // biome-ignore lint/a11y/noNoninteractiveElementInteractions: arrow-key roving between row buttons
        <ul ref={list} className="m-0 grid list-none gap-0.5 p-0" onKeyDown={onKey}>
          {visible.map(({ bark, agg }) => (
            <BarkRow
              key={bark.id}
              bark={bark}
              agg={agg}
              expanded={density === 'expanded'}
              queuedOffline={!!store?.isQueuedOffline(bark.id) && bark.status === 'queued'}
              onOpen={() => onInspect(`bark:${bark.id}`)}
              onUndo={canUndo(bark, now) ? () => void undo(bark) : undefined}
              onRedirect={() => onInspect(`bark:${bark.id}:redirect`)}
            />
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[14px] text-text-3">
          {rows.length
            ? 'Nothing here right now.'
            : 'What you tell this project shows up here, with what happened to each request.'}
        </p>
      )}
    </section>
  )
}
